// Compile-context equality helpers for incremental rebuild short-circuiting.
// Example: if contextAfter is equal, downstream unchanged entries can be reused.
import type { LuisCompileContext } from '../context'

// Deep-compare compile contexts to detect cross-source semantic changes.
export function areCompileContextsEqual(left: LuisCompileContext, right: LuisCompileContext): boolean {
  // Return false when class-variable maps are not equal.
  if (!areStringMapsEqual(left.classVariables, right.classVariables)) {
    return false
  } else if (!areObjectVariableMapsEqual(left.objectVariables, right.objectVariables)) {
    // Return false when object-variable maps are not equal.
    return false
  } else if (!areArrayVariableMapsEqual(left.arrayVariables, right.arrayVariables)) {
    // Return false when array-variable maps are not equal.
    return false
  } else {
    // Return true when all context maps are equal.
    return true
  }
}

// Compare flat string maps using key count and value equality.
function areStringMapsEqual(left: Record<string, string>, right: Record<string, string>): boolean {
  const leftKeys = Object.keys(left)
  const rightKeys = Object.keys(right)
  // Return false when key counts differ.
  if (leftKeys.length !== rightKeys.length) {
    return false
  } else {
    // Continue value-by-key comparison.
  }

  // Compare every key/value pair from left map against right map.
  for (const key of leftKeys) {
    // Return false when key is missing or value differs.
    if (!(key in right) || left[key] !== right[key]) {
      return false
    } else {
      // Continue map comparison for next key.
    }
  }

  // Return true when all key/value pairs match.
  return true
}

// Compare nested object-variable maps (`name -> { key: value }`).
function areObjectVariableMapsEqual(
  left: Record<string, Record<string, string>>,
  right: Record<string, Record<string, string>>,
): boolean {
  const leftKeys = Object.keys(left)
  const rightKeys = Object.keys(right)
  // Return false when top-level key counts differ.
  if (leftKeys.length !== rightKeys.length) {
    return false
  } else {
    // Continue nested object map comparison.
  }

  // Compare each nested object map recursively as flat string maps.
  for (const key of leftKeys) {
    // Return false when nested object entry is missing or unequal.
    if (!(key in right) || !areStringMapsEqual(left[key], right[key])) {
      return false
    } else {
      // Continue nested map checks for remaining keys.
    }
  }

  // Return true when all nested object maps match.
  return true
}

// Compare array-variable maps (`name -> string[]`) for exact value order.
function areArrayVariableMapsEqual(
  left: Record<string, string[]>,
  right: Record<string, string[]>,
): boolean {
  const leftKeys = Object.keys(left)
  const rightKeys = Object.keys(right)
  // Return false when top-level key counts differ.
  if (leftKeys.length !== rightKeys.length) {
    return false
  } else {
    // Continue array value comparison per key.
  }

  // Compare each array entry by length and ordered item equality.
  for (const key of leftKeys) {
    const leftValue = left[key]
    const rightValue = right[key]
    // Return false when array entry is missing or length differs.
    if (!rightValue || leftValue.length !== rightValue.length) {
      return false
    } else {
      // Continue element-by-element array comparison.
    }

    // Compare each array item in order for strict equality.
    for (let index = 0; index < leftValue.length; index += 1) {
      // Return false when any array item differs.
      if (leftValue[index] !== rightValue[index]) {
        return false
      } else {
        // Continue item comparison for remaining indexes.
      }
    }
  }

  // Return true when all array entries and items match.
  return true
}
